import { InputNumber } from '@repo/go-ui';
import { useTranslation } from 'react-i18next';

import { wrap } from 'src/packages/mobx-di/wrap';
import { hasValue } from 'src/packages/utils/has-value';

import type { MutableRefObject } from 'react';
import type { TFieldRulesData, TFieldView } from 'src/services/directory-service/types';

type Props = {
  fieldView: TFieldView;
  className?: string;
  innerRef?: MutableRefObject<HTMLInputElement | null>;
  error?: string;
  value: number | null;
  fieldRulesData?: TFieldRulesData;
  onChange(fieldId: string, value: unknown): void;
  onBlur?(fieldId: string): void;
};

export const NumberInput = wrap<Props>(function NumberInput({
  innerRef,
  fieldView,
  error,
  value,
  className,
  fieldRulesData,
  onChange,
  onBlur,
}) {
  const { t } = useTranslation();

  const handleChange = (value: string | number) => {
    if (!hasValue(value) || value === '') {
      onChange(fieldView.fieldId, null);
      return;
    }

    if (typeof value === 'number') {
      onChange(fieldView.fieldId, value);
      return;
    }

    const newValue = Number(value);

    if (!Number.isNaN(newValue)) {
      onChange(fieldView.fieldId, newValue);
    } else {
      onChange(fieldView.fieldId, null);
    }
  };

  const handleBlur = () => {
    onBlur?.(fieldView.fieldId);
  };

  return (
    <InputNumber
      ref={innerRef}
      className={className}
      value={value ?? ''}
      loading={fieldRulesData?.isLoading}
      status={error ? 'error' : undefined}
      placeholder={t('common:placeholders.enterValue')}
      disabled={fieldRulesData?.isDisabled || fieldRulesData?.isEnteringBlocked}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
});
